/**
 * What the promotion page SHOWS, as pure components (#3350): every value comes in
 * as a prop, so admin render tests can render exactly what an admin sees with no
 * network. PromotionShowPage (pages/promotions/show.tsx) fetches and passes data.
 *
 * Field names are the API's — the database's own column names — listed in
 * backend/src/routes/admin-promotions.ts (CLAIM/REDEMPTION/CODE_LIST_COLUMNS) and
 * getPromotionStats. tests/unit/admin/promotion-page-contract.test.ts pins the two
 * sides together; the page used to read names no endpoint sent.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '@/lib/utils';
import { RedemptionStatusBadge } from '@/components/coupons/RedemptionStatusBadge';

// ─── API shapes ───────────────────────────────────────────────────────────────

export interface PromotionStats {
  claims_total: number;
  claims_active: number;
  claims_expired: number;
  claims_revoked: number;
  coupons_granted: number;
  coupons_remaining: number;
  claims_used: number;
  redemptions_total: number;
  redemptions_held: number;
  redemptions_consumed: number;
  redemptions_refunded: number;
  unit_price_cents: number | null;
}

export interface ClaimListRow {
  id: string;
  user_id: string;
  status: 'active' | 'expired' | 'revoked';
  coupons_granted: number;
  coupons_remaining: number;
  source_event: string;
  expires_at: string | null;
  created_at: string;
  code: string | null;
}

export interface RedemptionListRow {
  id: string;
  claim_id: string;
  user_id: string;
  paywall_type: string;
  paywall_event_id: string;
  match_payment_id: string | null;
  status: 'held' | 'consumed' | 'refunded';
  held_at: string;
  consumed_at: string | null;
  refunded_at: string | null;
  refund_reason: string | null;
}

export interface CodeListRow {
  code: string;
  claimed_by_user_id: string | null;
  claimed_at: string | null;
  created_at: string;
}

export interface ListPage<Row> {
  rows: Row[];
  total: number;
  limit: number;
}

export interface CodesPage extends ListPage<CodeListRow> {
  claimed: number;
  available: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

/** Share of claims used at least once. A claim may hold several coupons, so it is claims, not redemptions. */
export function conversionText(stats: PromotionStats): string {
  if (stats.claims_total === 0) return '—';
  return `${((stats.claims_used / stats.claims_total) * 100).toFixed(1)}%`;
}

/** The value of the free coupons spent: consumed redemptions × the paywall's price. */
export function costEquivalentText(stats: PromotionStats): string {
  if (stats.redemptions_consumed === 0) return '—';
  if (stats.unit_price_cents === null) return `${stats.redemptions_consumed} consumed`;
  return formatCents(stats.redemptions_consumed * stats.unit_price_cents);
}

/** The same rule the API applies to the claims list's user filter; null = acceptable. */
export function userIdFilterProblem(input: string): string | null {
  const v = input.trim();
  if (v === '') return null;
  return /^[0-9a-f-]{1,36}$/i.test(v) ? null : 'Enter all or part of a user ID: digits 0-9, letters a-f and dashes.';
}

/** The page's own words for how much of a list it shows; `order` is the API's sort. */
function showing(count: number, total: number, noun: [string, string], order: 'newest' | 'oldest'): string {
  const word = (n: number) => (n === 1 ? noun[0] : noun[1]);
  return count < total
    ? `Showing the ${count} ${order} of ${total} ${word(total)}.`
    : `${total} ${word(total)}.`;
}

const UserLink = ({ id }: { id: string }) => (
  <Link to={`/users/${id}`} className="font-mono text-xs underline-offset-2 hover:underline">{id}</Link>
);

// ─── Overview KPIs ────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function PromotionKpis({ stats }: { stats: PromotionStats }) {
  const price = stats.unit_price_cents === null ? null : formatCents(stats.unit_price_cents);
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <KpiCard
        label="Total Claims"
        value={stats.claims_total}
        sub={`${stats.claims_active} active · ${stats.claims_expired} expired · ${stats.claims_revoked} revoked`}
      />
      <KpiCard
        label="Consumed"
        value={stats.redemptions_consumed}
        sub={`${stats.redemptions_held} held · ${stats.redemptions_refunded} refunded`}
      />
      <KpiCard label="Conversion" value={conversionText(stats)} sub="claims used at least once" />
      <KpiCard
        label="Cost Equivalent"
        value={costEquivalentText(stats)}
        sub={price
          ? `${stats.redemptions_consumed} consumed × ${price} · ${stats.redemptions_total} total redemptions`
          : `${stats.redemptions_total} total redemptions`}
      />
    </div>
  );
}

// ─── Tables ───────────────────────────────────────────────────────────────────

type ListState = { loading: boolean; error: string | null };

function StateRow({ cols, children }: { cols: number; children: ReactNode }) {
  return <tr><td colSpan={cols} className="px-4 py-8 text-center text-muted-foreground">{children}</td></tr>;
}

const CLAIM_STATUS_CLASS: Record<ClaimListRow['status'], string> = {
  active: 'border-green-200 bg-green-100 text-green-700',
  revoked: 'border-red-200 bg-red-100 text-red-700',
  expired: 'border-gray-200 bg-gray-100 text-gray-600',
};

export function ClaimsTable({ page, loading, error }: { page: ListPage<ClaimListRow> | null } & ListState) {
  const rows = page?.rows ?? [];
  return (
    <div className="space-y-2">
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Claim ID</th>
              <th className="px-4 py-3 font-medium">User</th>
              <th className="px-4 py-3 font-medium">Code</th>
              <th className="px-4 py-3 font-medium text-center">Coupons left</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Claimed at</th>
              <th className="px-4 py-3 font-medium">Expires</th>
            </tr>
          </thead>
          <tbody>
            {loading && <StateRow cols={7}>Loading…</StateRow>}
            {!loading && error && <StateRow cols={7}><span className="text-destructive">Could not load claims: {error}</span></StateRow>}
            {!loading && !error && rows.length === 0 && <StateRow cols={7}>No claims match.</StateRow>}
            {!loading && !error && rows.map((c) => (
              <tr key={c.id} className="border-b hover:bg-muted/30 transition">
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{c.id}</td>
                <td className="px-4 py-3"><UserLink id={c.user_id} /></td>
                <td className="px-4 py-3">
                  {c.code
                    ? <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{c.code}</code>
                    : <span className="text-muted-foreground text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-center">{c.coupons_remaining} of {c.coupons_granted}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${CLAIM_STATUS_CLASS[c.status] ?? CLAIM_STATUS_CLASS.expired}`}>{c.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(c.created_at)}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{c.expires_at ? formatDate(c.expires_at) : 'Never'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {page && !loading && !error && rows.length > 0 && (
        <p className="text-xs text-muted-foreground">{showing(rows.length, page.total, ['claim', 'claims'], 'newest')}</p>
      )}
    </div>
  );
}

export function RedemptionsTable({ page, loading, error }: { page: ListPage<RedemptionListRow> | null } & ListState) {
  const rows = page?.rows ?? [];
  return (
    <div className="space-y-2">
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Redemption ID</th>
              <th className="px-4 py-3 font-medium">User</th>
              <th className="px-4 py-3 font-medium">Paywall event</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Redeemed at</th>
              <th className="px-4 py-3 font-medium">Resolved at</th>
            </tr>
          </thead>
          <tbody>
            {loading && <StateRow cols={6}>Loading…</StateRow>}
            {!loading && error && <StateRow cols={6}><span className="text-destructive">Could not load redemptions: {error}</span></StateRow>}
            {!loading && !error && rows.length === 0 && <StateRow cols={6}>No redemptions recorded.</StateRow>}
            {!loading && !error && rows.map((r) => (
              <tr key={r.id} className="border-b hover:bg-muted/30 transition">
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.id}</td>
                <td className="px-4 py-3"><UserLink id={r.user_id} /></td>
                <td className="px-4 py-3"><code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{r.paywall_event_id}</code></td>
                <td className="px-4 py-3"><RedemptionStatusBadge status={r.status} size="sm" /></td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(r.held_at)}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                  {r.consumed_at ? formatDate(r.consumed_at) : r.refunded_at ? formatDate(r.refunded_at) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {page && !loading && !error && rows.length > 0 && (
        <p className="text-xs text-muted-foreground">{showing(rows.length, page.total, ['redemption', 'redemptions'], 'newest')}</p>
      )}
    </div>
  );
}

export function CodesSummary({ page }: { page: CodesPage | null }) {
  if (!page) return <p className="text-sm text-muted-foreground">Loading codes…</p>;
  return (
    <p className="text-sm text-muted-foreground">
      {page.total} code{page.total !== 1 ? 's' : ''} · {page.claimed} claimed · {page.available} available
    </p>
  );
}

export function CodesTable({ page, loading, error }: { page: CodesPage | null } & ListState) {
  const rows = page?.rows ?? [];
  return (
    <div className="space-y-2">
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Code</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Claimed by</th>
              <th className="px-4 py-3 font-medium">Claimed at</th>
            </tr>
          </thead>
          <tbody>
            {loading && <StateRow cols={4}>Loading…</StateRow>}
            {!loading && error && <StateRow cols={4}><span className="text-destructive">Could not load codes: {error}</span></StateRow>}
            {!loading && !error && rows.length === 0 && <StateRow cols={4}>No codes generated yet. Use "Generate more" to create codes.</StateRow>}
            {!loading && !error && rows.map((c) => (
              <tr key={c.code} className="border-b hover:bg-muted/30 transition">
                <td className="px-4 py-3"><code className="rounded bg-muted px-2 py-1 font-mono text-sm">{c.code}</code></td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                    c.claimed_by_user_id ? 'border-green-200 bg-green-100 text-green-700' : 'border-gray-200 bg-gray-100 text-gray-600'
                  }`}>
                    {c.claimed_by_user_id ? 'Claimed' : 'Available'}
                  </span>
                </td>
                <td className="px-4 py-3">{c.claimed_by_user_id ? <UserLink id={c.claimed_by_user_id} /> : <span className="text-muted-foreground text-xs">—</span>}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{c.claimed_at ? formatDate(c.claimed_at) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {page && !loading && !error && rows.length > 0 && (
        <p className="text-xs text-muted-foreground">{showing(rows.length, page.total, ['code', 'codes'], 'oldest')}</p>
      )}
    </div>
  );
}
